// DarkNetwork.jsx
import { useEffect, useRef, useState } from "react";
import "../../styles/navbar/DarkNetwork.css";
import { useMamdani } from "../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

const POLITICIANS = [
  { id: 'netanyahu',   name: 'נתניהו' },
  { id: 'YoavKisch',  name: 'יואב קיש' },
  { id: 'AmirOhana',  name: 'אוחנה' },
  { id: 'zoharm7',    name: 'זוהר' },
  { id: 'regev_miri', name: 'רגב' },
  { id: 'Likud_Party',name: 'הליכוד' },
];

const THREAT_COLOR = {
  CRITICAL: '#ff2244',
  HIGH:     '#ff8c00',
  MEDIUM:   '#ffd700',
};

// ─────────────────────────────────────────
// פונקציית ייצוא דוח - פותחת טאב חדש
// ─────────────────────────────────────────
function exportReport(networks, politicianName, stats) {
  if (!networks || networks.length === 0) return;

  const date = new Date().toLocaleDateString('he-IL', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const THREAT_LABEL    = { CRITICAL: 'קריטי', HIGH: 'גבוה', MEDIUM: 'בינוני', LOW: 'נמוך' };
  const THREAT_COLOR_MAP = { CRITICAL: '#ff2244', HIGH: '#ff8c00', MEDIUM: '#ffd700', LOW: '#00cfff' };

  const tableRows = networks.map((net, i) => {
    const color = THREAT_COLOR_MAP[net.threat] || '#ffd700';
    const label = THREAT_LABEL[net.threat] || net.threat;
    const barW  = net.score;
    return `
      <tr>
        <td>${i + 1}</td>
        <td><b>${net.name}</b></td>
        <td style="font-family:monospace;color:#4a8090">${net.address}</td>
        <td style="text-align:center;color:${color}">${net.bots}</td>
        <td><span style="color:${color}">${label} ${barW}%</span></td>
        <td style="text-align:center">${net.score}</td>
      </tr>`;
  }).join('');

  const totalBots = stats?.total_bots ?? networks.reduce((s, n) => s + n.bots, 0);

  const html = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8"/>
  <title>דוח מפת מקורות איום – ${politicianName}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { background:#050c18; color:#c8d8f0; font-family:'Rubik',sans-serif; padding:40px; }
    h1 { font-size:24px; color:#e8f4ff; margin-bottom:4px; }
    h1 span { color:#00cfff; }
    .tag { font-size:10px; color:#ff2244; letter-spacing:3px; margin-bottom:8px; }
    .sub { font-size:11px; color:#4a6080; margin-bottom:28px; }
    .stats { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:28px; }
    .stat { background:#0a1628; border:1px solid #ffffff0f; border-radius:8px; padding:14px; text-align:center; }
    .stat .v { font-size:26px; font-weight:700; font-family:monospace; }
    .stat .l { font-size:10px; color:#4a6080; margin-top:4px; }
    table { width:100%; border-collapse:collapse; }
    th { background:#0a1628; padding:10px 14px; font-size:10px; letter-spacing:2px; color:#4a7090; text-align:right; border-bottom:1px solid rgba(0,255,255,0.12); }
    td { padding:10px 14px; border-bottom:1px solid rgba(255,255,255,0.03); }
    tr:nth-child(even) { background:#06101e; }
    .footer { margin-top:32px; padding-top:16px; border-top:1px solid rgba(255,255,255,0.04); font-size:11px; color:#2a4060; font-family:monospace; }
    .print-btn { position:fixed; bottom:24px; left:24px; background:#00cfff; color:#050c18; border:none; border-radius:8px; padding:12px 22px; font-size:14px; font-weight:700; cursor:pointer; box-shadow:0 4px 20px rgba(0,207,255,0.3); }
    .print-btn:hover { background:#33d8ff; }
    @media print { .print-btn { display:none; } * { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
  </style>
</head>
<body>
  <button class="print-btn" onclick="window.print()">שמור כ-PDF</button>
  <div class="tag">THREAT SOURCE MAP REPORT</div>
  <h1>דוח מפת מקורות איום — <span>${politicianName}</span></h1>
  <div class="sub">נוצר: ${date} | AntiBot Platform v2.1</div>
  <div class="stats">
    <div class="stat"><div class="v" style="color:#ff2244">${totalBots.toLocaleString()}</div><div class="l">בוטים זוהו</div></div>
    <div class="stat"><div class="v" style="color:#ff8c00">${stats?.active ?? '—'}</div><div class="l">רשתות פעילות</div></div>
    <div class="stat"><div class="v" style="color:#00cfff">${stats?.networks ?? networks.length}</div><div class="l">מדינות</div></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>שם רשת</th><th>כתובת IP</th><th>בוטים</th><th>רמת איום</th><th>ציון</th></tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div class="footer">REPORT ID: DNR-${Date.now().toString(36).toUpperCase()} | ${politicianName} | סווג: שימוש פנימי בלבד</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

// ─────────────────────────────────────────
// גלובוס Canvas
// ─────────────────────────────────────────
function Globe({ networks }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width  = 220;
    const H = canvas.height = 220;
    const cx = W / 2, cy = H / 2, R = 88;

    const COORDS = {
      GB:[-51.5,  -0.1], US:[38.9,  -77.0], IR:[35.7,  51.4],
      RU:[55.7,   37.6], CN:[39.9, 116.4],  DE:[52.5,  13.4],
      FR:[48.8,    2.3], FI:[60.1,  24.9],  BY:[53.9,  27.5],
      UA:[50.4,   30.5], TR:[39.9,  32.9],  SA:[24.7,  46.7],
      YE:[15.3,   44.2], ID:[-6.2, 106.8],  AU:[-35.3,149.1],
      CA:[45.4,  -75.7], ES:[40.4,  -3.7],  IT:[41.9,  12.5],
      IL:[31.8,   35.2],
    };

    // דגלים לפי קוד מדינה
    const FLAGS = {
      GB:'🇬🇧', US:'🇺🇸', IR:'🇮🇷', RU:'🇷🇺', CN:'🇨🇳',
      DE:'🇩🇪', FR:'🇫🇷', FI:'🇫🇮', BY:'🇧🇾', UA:'🇺🇦',
      TR:'🇹🇷', SA:'🇸🇦', YE:'🇾🇪', ID:'🇮🇩', AU:'🇦🇺',
      CA:'🇨🇦', ES:'🇪🇸', IT:'🇮🇹', IL:'🇮🇱',
    };

    const NAMES = {
      GB:'בריטניה', US:'ארה״ב',  IR:'איראן',    RU:'רוסיה',
      CN:'סין',     DE:'גרמניה', FR:'צרפת',      FI:'פינלנד',
      BY:'בלארוס',  UA:'אוקראינה', TR:'טורקיה', SA:'סעודיה',
      YE:'תימן',    ID:'אינדונזיה', AU:'אוסטרליה', CA:'קנדה',
      ES:'ספרד',    IT:'איטליה', IL:'ישראל',
    };

    const THREAT_COLOR_GLOBE = {
      CRITICAL: '#ff2244',
      HIGH:     '#ff8c00',
      MEDIUM:   '#ffd700',
      LOW:      '#00cfff',
    };

    const points = (networks || []).map(net => {
      const coords = COORDS[net.code] || [0, 0];
      return {
        lat:   coords[0],
        lon:   coords[1],
        color: THREAT_COLOR_GLOBE[net.threat] || '#ff8c00',
        size:  3 + Math.min(net.bots / 20, 6),
        flag:  FLAGS[net.code]  || '🏳️',
        name:  NAMES[net.code]  || net.code,
        bots:  net.bots,
      };
    });

    function project(lat, lon, t) {
      const phi   = (90 - lat) * Math.PI / 180;
      const theta = (lon + t) * Math.PI / 180;
      return {
        x: cx + R * Math.sin(phi) * Math.cos(theta),
        y: cy + R * Math.cos(phi),
        z: Math.sin(phi) * Math.sin(theta),
      };
    }

    let t = 0;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      t += 0.3;

      // כדור
      const grad = ctx.createRadialGradient(cx-20, cy-20, 10, cx, cy, R);
      grad.addColorStop(0, 'rgba(0,60,120,0.9)');
      grad.addColorStop(0.5, 'rgba(0,30,80,0.85)');
      grad.addColorStop(1, 'rgba(0,10,40,0.9)');
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // קווי רשת
      for (let lat = -60; lat <= 60; lat += 30) {
        ctx.beginPath();
        for (let lon = -180; lon <= 180; lon += 5) {
          const p = project(lat, lon, t);
          if (p.z > 0) lon === -180 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
        }
        ctx.strokeStyle = 'rgba(0,150,255,0.12)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      for (let lon = -180; lon <= 180; lon += 30) {
        ctx.beginPath();
        for (let lat = -80; lat <= 80; lat += 5) {
          const p = project(lat, lon, t);
          if (p.z > 0) lat === -80 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
        }
        ctx.strokeStyle = 'rgba(0,150,255,0.12)';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      const israel = project(31.8, 35.2, t);

      // ציור נקודות + דגלים + שמות
      points.forEach(pt => {
        const p = project(pt.lat, pt.lon, t);
        if (p.z < 0) return;

        // קו לישראל
        if (israel.z > 0) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(israel.x, israel.y);
          ctx.strokeStyle = `${pt.color}44`;
          ctx.lineWidth = 0.7;
          ctx.stroke();
        }

        // הילה חיצונית
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pt.size * 3);
        glow.addColorStop(0, `${pt.color}66`);
        glow.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(p.x, p.y, pt.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // נקודה מרכזית
        ctx.beginPath();
        ctx.arc(p.x, p.y, pt.size, 0, Math.PI * 2);
        ctx.fillStyle = pt.color;
        ctx.fill();

        // דגל מעל הנקודה
        ctx.font = '11px serif';
        ctx.textAlign = 'center';
        ctx.fillText(pt.flag, p.x, p.y - pt.size - 2);

        // שם מדינה מתחת לדגל (קטן, עם רקע שקוף)
        ctx.font = 'bold 7px sans-serif';
        ctx.textAlign = 'center';
        const tw = ctx.measureText(pt.name).width;

        // רקע לטקסט
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(p.x - tw/2 - 2, p.y - pt.size - 22, tw + 4, 9);

        ctx.fillStyle = pt.color;
        ctx.fillText(pt.name, p.x, p.y - pt.size - 15);
      });

      // ישראל - נקודה ירוקה עם דגל
      if (israel.z > 0) {
        const g = ctx.createRadialGradient(israel.x, israel.y, 0, israel.x, israel.y, 12);
        g.addColorStop(0, '#00ff64');
        g.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(israel.x, israel.y, 12, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(israel.x, israel.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#00ff64';
        ctx.fill();

        // דגל ישראל
        ctx.font = '12px serif';
        ctx.textAlign = 'center';
        ctx.fillText('🇮🇱', israel.x, israel.y - 14);
      }

      // גבול כדור
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(0,150,255,0.3)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [networks]);

  return <canvas ref={canvasRef} className="dn-globe" />;
}

const BOT_TYPE_COLOR = {
  BOT:        '#ff2244',
  LIKELY_BOT: '#ff7a00',
  SUSPICIOUS: '#ffd600',
  REVIEW:     '#7eceff',
};

// ─────────────────────────────────────────
// קומפוננטה ראשית
// ─────────────────────────────────────────
export default function DarkNetwork() {
  const [activeTab, setActiveTab]   = useState('netanyahu');
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [exported, setExported]     = useState(false);
  const [search, setSearch]         = useState('');
  const [activeAccIdx, setAccIdx]   = useState(0);

  const { mamdaniMode: mm, data: mData, stats: mst } = useMamdani();

  // ── שליפה רגילה ──
  useEffect(() => {
    if (mm) return;
    setLoading(true);
    fetch(`${API_BASE}/api/cyber/dark-network/${activeTab}`)
      .then(r => r.json())
      .then(json => { if (json.success) setData(json); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeTab, mm]);

  const stats              = data?.stats;
  const currentPolitician  = POLITICIANS.find(p => p.id === activeTab)?.name || activeTab;

  // ── ייצוא רגיל ──
  function handleExport() {
    if (mm) {
      exportMamdaniReport(mData.allBots, mst);
    } else {
      exportReport(data?.networks, currentPolitician, data?.stats);
    }
    setExported(true);
    setTimeout(() => setExported(false), 2000);
  }

  // ── ייצוא ממדאני ──
  function exportMamdaniReport(bots, stats) {
    const date  = new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });
    const rows  = bots.map((b, i) => `
      <tr>
        <td>${i+1}</td>
        <td><b>@${b.username}</b></td>
        <td style="color:${BOT_TYPE_COLOR[b.bot_type]||'#ffd700'}">${b.bot_type}</td>
        <td style="text-align:center">${b.bot_score}/100</td>
        <td style="color:#ff6040">${b.anti_israel_count > 0 ? `⚠ ${b.anti_israel_count}/50` : '—'}</td>
        <td style="color:#a070ff">${b.is_coordinated ? 'COORDINATED' : '—'}</td>
        <td style="font-size:10px;color:#8899aa">${(b.reasons||[]).slice(0,2).join(', ')}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/>
<title>Mamdani Bot Report</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { background:#050c18; color:#c8d8f0; font-family:sans-serif; padding:40px; }
h1 { font-size:24px; color:#e8f4ff; margin-bottom:4px; }
h1 span { color:#ff6040; }
.sub { font-size:11px; color:#4a6080; margin-bottom:28px; }
.stats { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:28px; }
.stat { background:#0a1628; border:1px solid #ffffff0f; border-radius:8px; padding:14px; text-align:center; }
.stat .v { font-size:26px; font-weight:700; font-family:monospace; }
.stat .l { font-size:10px; color:#4a6080; margin-top:4px; }
table { width:100%; border-collapse:collapse; }
th { background:#0a1628; padding:10px 14px; font-size:10px; letter-spacing:2px; color:#4a7090; border-bottom:1px solid rgba(0,255,255,0.12); }
td { padding:9px 14px; border-bottom:1px solid rgba(255,255,255,0.03); font-size:12px; }
tr:nth-child(even) { background:#06101e; }
.print-btn { position:fixed; bottom:24px; right:24px; background:#ff6040; color:#fff; border:none; border-radius:8px; padding:12px 22px; font-size:14px; font-weight:700; cursor:pointer; }
@media print { .print-btn { display:none; } }
</style></head>
<body>
<button class="print-btn" onclick="window.print()">Save as PDF</button>
<h1>Mamdani Bot Network — <span>Anti-Israel Detection Report</span></h1>
<div class="sub">Generated: ${date} | AntiBot Platform</div>
<div class="stats">
  <div class="stat"><div class="v" style="color:#ff2244">${stats.totalBots}</div><div class="l">Total Bots</div></div>
  <div class="stat"><div class="v" style="color:#ff6040">${stats.antiIsrael}</div><div class="l">Anti-Israel</div></div>
  <div class="stat"><div class="v" style="color:#a070ff">${stats.coordinated}</div><div class="l">Coordinated</div></div>
  <div class="stat"><div class="v" style="color:#00cfff">${stats.accounts}</div><div class="l">Accounts Targeted</div></div>
</div>
<table>
<thead><tr><th>#</th><th>Username</th><th>Type</th><th>Score</th><th>Anti-Israel</th><th>Coordinated</th><th>Reasons</th></tr></thead>
<tbody>${rows}</tbody>
</table>
</body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  }

  // ── מוד ממדאני — נתונים לחשבון הנבחר ──
  const selectedAcc  = mData.accounts[activeAccIdx] || null;
  const accBots      = selectedAcc?.bots || [];
  const filteredBots = accBots.filter(b => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return b.username.toLowerCase().includes(q) ||
           (b.location || '').toLowerCase().includes(q) ||
           b.bot_type.toLowerCase().includes(q);
  });

  return (
    <div className="dn-page">

      {/* ── כותרת ── */}
      <h1 className="dn-title">
        {mm ? 'Mamdani — Dark Network Scan' : 'מפת מקורות איום'}
      </h1>

      {/* ── טאבים ── */}
      <div className="dn-tabs">
        {mm
          ? mData.accounts.map((acc, i) => (
              <button
                key={acc.username}
                className={`dn-tab ${activeAccIdx === i ? 'active' : ''}`}
                onClick={() => { setAccIdx(i); setSearch(''); }}
              >
                @{acc.username}
                {acc.bots.length > 0 && (
                  <span style={{ marginRight: 5, fontSize: 10, color: '#ff6040' }}>
                    {acc.bots.length}
                  </span>
                )}
              </button>
            ))
          : POLITICIANS.map(p => (
              <button
                key={p.id}
                className={`dn-tab ${activeTab === p.id ? 'active' : ''}`}
                onClick={() => setActiveTab(p.id)}
              >
                {p.name}
              </button>
            ))
        }
      </div>

      <div className="dn-body">

        {/* ── פאנל שמאל ── */}
        <div className="dn-left">
          <div className="dn-globe-section">
            <div className="dn-globe-header">
              <span>{mm ? 'Bot Activity' : 'מפת איומים גלובלית'}</span>
              {!mm && (
                <span className="dn-updated">
                  {data?.last_analyzed
                    ? `עודכן: ${new Date(data.last_analyzed).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                    : "—"}
                </span>
              )}
            </div>
            <Globe networks={mm ? [] : data?.networks} />
          </div>

          {/* סטטיסטיקות */}
          <div className="dn-stats">
            {mm ? (
              <>
                <div className="dn-stat red">
                  <span className="dn-stat-val">{mst.totalBots}</span>
                  <span className="dn-stat-lbl">Bots Found</span>
                </div>
                <div className="dn-stat yellow">
                  <span className="dn-stat-val">{mst.antiIsrael}</span>
                  <span className="dn-stat-lbl">Anti-Israel</span>
                </div>
                <div className="dn-stat" style={{ color: '#a070ff' }}>
                  <span className="dn-stat-val">{mst.coordinated}</span>
                  <span className="dn-stat-lbl">Coordinated</span>
                </div>
                <div className="dn-stat blue">
                  <span className="dn-stat-val">{accBots.length}</span>
                  <span className="dn-stat-lbl">This Account</span>
                </div>
              </>
            ) : (
              <>
                <div className="dn-stat yellow">
                  <span className="dn-stat-val">{stats?.active ?? '—'}</span>
                  <span className="dn-stat-lbl">במעקב</span>
                </div>
                <div className="dn-stat red">
                  <span className="dn-stat-val">{stats?.networks ?? '—'}</span>
                  <span className="dn-stat-lbl">איומים פעילים</span>
                </div>
                <div className="dn-stat blue">
                  <span className="dn-stat-val">{stats?.total_bots?.toLocaleString() ?? '—'}</span>
                  <span className="dn-stat-lbl">בוטים זוהו</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── פאנל ימין ── */}
        <div className="dn-right">
          <div className="dn-table-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>{mm ? `Bots on @${selectedAcc?.username || ''}` : 'תוצאות סריקה'}</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={mm ? '🔍 Search username / type...' : '🔍 חיפוש IP / מדינה / רשת...'}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px', padding: '5px 12px',
                color: '#fff', fontSize: '12px', width: '200px', outline: 'none',
              }}
            />
          </div>

          {/* ── טבלת ממדאני ── */}
          {mm && (
            <div className="dn-table-wrap">
              <table className="dn-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Username</th>
                    <th>Score</th>
                    <th>Anti-Israel</th>
                    <th>Coordinated</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBots.map((b, i) => {
                    const tc = BOT_TYPE_COLOR[b.bot_type] || '#ffd700';
                    return (
                      <tr key={i}>
                        <td>
                          <span style={{ color: tc, fontWeight: 700, fontSize: 11 }}>{b.bot_type}</span>
                        </td>
                        <td className="dn-network-name">
                          <a href={`https://twitter.com/${b.username}`} target="_blank" rel="noreferrer"
                            style={{ color: '#c8d8f0', textDecoration: 'none' }}>
                            @{b.username}
                          </a>
                        </td>
                        <td>
                          <span className="dn-bots-badge" style={{ color: tc }}>{b.bot_score}/100</span>
                        </td>
                        <td>
                          {b.anti_israel_count > 0
                            ? <span style={{ color: '#ff6040', fontWeight: 700 }}>⚠ {b.anti_israel_count}/50</span>
                            : <span style={{ color: '#4a6080' }}>—</span>
                          }
                        </td>
                        <td>
                          {b.is_coordinated
                            ? <span style={{ color: '#a070ff', fontWeight: 700 }}>COORDINATED</span>
                            : <span style={{ color: '#4a6080' }}>—</span>
                          }
                        </td>
                        <td className="dn-address" style={{ fontSize: 11 }}>
                          {b.location || '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredBots.length === 0 && (
                    <tr><td colSpan={6} className="dn-empty">No bots found for this account</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── טבלה רגילה ── */}
          {!mm && (
            loading ? <div className="dn-loading">סורק...</div> : (
              <div className="dn-table-wrap">
                <table className="dn-table">
                  <thead>
                    <tr><th>רשת</th><th>כתובת</th><th>בוטים</th><th>רמת איום</th></tr>
                  </thead>
                  <tbody>
                    {data?.networks?.filter(net => {
                      if (!search.trim()) return true;
                      const q = search.toLowerCase();
                      return net.name?.toLowerCase().includes(q) ||
                             net.address?.toLowerCase().includes(q) ||
                             net.code?.toLowerCase().includes(q);
                    }).map((net, i) => {
                      const tc = THREAT_COLOR[net.threat] || '#ffd700';
                      return (
                        <tr key={i}>
                          <td className="dn-network-name">{net.name}</td>
                          <td className="dn-address">{net.address}</td>
                          <td><span className="dn-bots-badge" style={{ color: tc }}>{net.bots}</span></td>
                          <td>
                            <div className="dn-threat-wrap">
                              <div className="dn-threat-bar" style={{
                                width: `${net.score}%`,
                                background: tc
                              }} />
                              <span style={{ color: tc, fontSize: 11 }}>
                                {net.score}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {!data?.networks?.length && !loading && (
                      <tr><td colSpan={4} className="dn-empty">לא נמצאו רשתות חשודות</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )
          )}

          <div className="dn-footer-btns">
            <button
              className={`dn-footer-btn blue ${exported ? 'exported' : ''}`}
              onClick={handleExport}
              disabled={mm ? mData.allBots.length === 0 : (!data?.networks?.length || loading)}
            >
              {exported ? '✓ Opened in new tab!' : '📄 Export Report'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}